import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Divider, Form, Space, Button, notification, Row, Col, Input as AntInput } from 'antd'
import { Input, Select, InputPassword } from '../../components'
import { useTranslation } from 'react-i18next'
import { DisplayUserName } from '../../constants'
import { formatDisplayName } from '../../utils/displayName'
import { UserExternalFlags, UserProfile, UserDetails } from '../../types/user'
import { SystemContext } from '../../contexts'
import { apiCall } from '../../services'
import { useStyles } from './UserDetailsTab.styles'

interface UserDetailsTabProps {
    isNewUser?: boolean | undefined
    displayPasswordField?: boolean | undefined
    externalFlags?: UserExternalFlags | undefined
    showResendVerification?: boolean | undefined
    userProfile?: UserProfile | UserDetails | undefined
    resendLoading?: boolean | undefined
    cooldown?: number | undefined
    onResendVerification?: (() => void) | undefined
    requireEmailAndDisplayName?: boolean | undefined
}

export const UserDetailsTab: FC<UserDetailsTabProps> = ({
    isNewUser,
    externalFlags,
    displayPasswordField = true,
    showResendVerification = false,
    userProfile,
    resendLoading,
    cooldown,
    onResendVerification,
    requireEmailAndDisplayName = false,
}) => {
    const { t } = useTranslation()
    const { styles } = useStyles()
    const form = Form.useFormInstance()
    const { isExternalAuthSystem, systemSettings } = useContext(SystemContext)
    const firstName = Form.useWatch('firstName', form)
    const lastName = Form.useWatch('lastName', form)
    const [isDisplayNameFieldDisabled, setIsDisplayNameFieldDisabled] = useState<boolean>(true)
    const previousDisplayNameSelect = useRef('')
    // Use parent-provided resend logic if available, otherwise manage locally
    const [localResendLoading, setLocalResendLoading] = useState(false)
    const [localCooldown, setLocalCooldown] = useState(0)
    const resendLoadingToUse = typeof resendLoading === 'boolean' ? resendLoading : localResendLoading
    const cooldownToUse = typeof cooldown === 'number' ? cooldown : localCooldown

    // Track if form is dirty (any field changed)
    const watchedFields = Form.useWatch([], form)
    const [isFormDirty, setIsFormDirty] = useState(false)
    useEffect(() => {
        setIsFormDirty(form.isFieldsTouched())
    }, [watchedFields, form])

    const handleResend = async () => {
        if (onResendVerification) {
            onResendVerification()
            return
        }
        if (!userProfile?.username) {
            notification.error({ title: t('users:cannot_determine_current_user') })
            return
        }
        setLocalResendLoading(true)
        try {
            await apiCall(`/mail/send/${userProfile.username}`, { method: 'POST' }, true)
            notification.success({ title: t('users:verification_email_sent') })
            setLocalCooldown(60)
        } catch (_) {
            notification.error({ title: t('users:failed_to_send_verification_email') })
        } finally {
            setLocalResendLoading(false)
        }
    }
    React.useEffect(() => {
        let timer: NodeJS.Timeout | undefined
        if (!cooldown && localCooldown > 0) {
            timer = setInterval(() => {
                setLocalCooldown((prev) => (prev > 0 ? prev - 1 : 0))
            }, 1000)
        }
        return () => {
            if (timer) clearInterval(timer)
        }
    }, [localCooldown, cooldown])

    const displayNameOptions = useMemo(() => (
        [
            {
                value: DisplayUserName.FirstLast,
                label: t('users:first_last'),
            },
            {
                value: DisplayUserName.LastFirst,
                label: t('users:last_first'),
            },
            {
                value: DisplayUserName.Other,
                label: t('users:other'),
            },
        ]
    ), [t])

    const isResendVerificationButtonVisible: Boolean = useMemo(() => {
        return !!(showResendVerification && userProfile && userProfile.externalFlags && !userProfile.externalFlags.emailVerified && systemSettings?.supportedFeatures?.emailVerification)
    }, [showResendVerification, userProfile, systemSettings])

    useEffect(() => {
        const formatted = formatDisplayName(form.getFieldValue('displayNameSelect'), firstName, lastName)
        if (formatted !== null) {
            form.setFieldsValue({ displayName: formatted })
        }
        form.validateFields(['displayName'])
    }, [firstName, lastName])

    return (
        <>
            {isNewUser && (
                <div aria-hidden="true" style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}>
                    <input autoComplete="username" name="fake-username" tabIndex={-1} type="text" />
                    <input autoComplete="current-password" name="fake-password" tabIndex={-1} type="password" />
                </div>
            )}
            <Divider titlePlacement="start">{t('users:account')}</Divider>
            <Input
                {...(isNewUser && { autoComplete: 'off' })}
                disabled={!isNewUser}
                label={t('users:edit_modal.username')}
                name="username"
                rules={[{
                    required: !!isNewUser,
                    message: t('users:edit_modal.username_required')
                }]}
            />
            <Form.Item
                label={t('users:edit_modal.email')}
                required={requireEmailAndDisplayName}
                style={displayPasswordField ? { marginBottom: 24 } : {}}
            >
                <Row align="top" className={styles.emailRow} gutter={8} style={{ width: '100%', height: 32 }}>
                    <Col className={isResendVerificationButtonVisible ? styles.emailInput : ''} flex="auto">
                        <Form.Item
                            noStyle
                            name="email"
                            rules={[
                                {
                                    required: requireEmailAndDisplayName,
                                    whitespace: requireEmailAndDisplayName,
                                    message: t('users:edit_modal.email_required'),
                                },
                                {
                                    type: 'email',
                                    message: t('users:edit_modal.email_invalid')
                                },
                                {
                                    max: 254,
                                    message: t('users:edit_modal.email_max_length')
                                },
                            ]}
                        >
                            <AntInput
                                {...(isNewUser && { autoComplete: 'off' })}
                                disabled={!!externalFlags?.emailExternal}
                                type="email"
                            />
                        </Form.Item>
                    </Col>
                    {isResendVerificationButtonVisible && (
                        <Col>
                            <Button
                                disabled={cooldownToUse > 0 || isFormDirty}
                                loading={resendLoadingToUse}
                                onClick={handleResend}
                                type="primary"
                            >
                                {t('users:resend_verification_email')}
                            </Button>
                            {cooldownToUse > 0 && (
                                <div style={{ color: '#888', fontSize: 12, textAlign: 'right' }}>
                                    {t('users:resend_verification_email_timer', { seconds: cooldownToUse })}
                                </div>
                            )}
                        </Col>
                    )}
                </Row>
            </Form.Item>
            {displayPasswordField && !isExternalAuthSystem && (
                <InputPassword
                    autoComplete="new-password"
                    label={t('users:edit_modal.password')}
                    name="password"
                    rules={[{
                        required: !!isNewUser,
                        message: t('users:edit_modal.password_required')
                    }]}
                />
            )}
            <Divider titlePlacement="start">{t('users:name')}</Divider>
            <Input
                disabled={!!externalFlags?.firstNameExternal}
                label={t('users:edit_modal.first_name')}
                name="firstName"
                tooltip={t('users:edit_modal.first_name_info')}
                rules={[{
                    max: 25,
                    message: t('users:edit_modal.first_name_max_length')
                }]}
            />
            <Input
                disabled={!!externalFlags?.lastNameExternal}
                label={t('users:edit_modal.last_name')}
                name="lastName"
                tooltip={t('users:edit_modal.last_name_info')}
                rules={[{
                    max: 25,
                    message: t('users:edit_modal.last_name_max_length')
                }]}
            />
            <Form.Item
                label={t('users:edit_modal.display_name')}
                required={requireEmailAndDisplayName}
            >
                <Space.Compact>
                    <Select
                        disabled={!!externalFlags?.displayNameExternal}
                        name="displayNameSelect"
                        options={displayNameOptions}
                        style={{ width: 120 }}
                    />
                    <Input
                        dependencies={['displayNameSelect']}
                        disabled={isDisplayNameFieldDisabled || !!externalFlags?.displayNameExternal}
                        name="displayName"
                        style={{ width: 248 }}
                        rules={[
                            {
                                required: requireEmailAndDisplayName,
                                whitespace: requireEmailAndDisplayName,
                                message: t('users:edit_modal.display_name_required'),
                            },
                            {
                                max: 64,
                                message: t('users:edit_modal.display_name_max_length')
                            },
                            // @ts-ignore
                            ({ getFieldValue, setFieldValue }) => {
                                if (previousDisplayNameSelect.current !== getFieldValue('displayNameSelect')) {
                                    previousDisplayNameSelect.current = getFieldValue('displayNameSelect')
                                    if (getFieldValue('displayNameSelect') === DisplayUserName.Other) {
                                        setIsDisplayNameFieldDisabled(false)
                                    } else {
                                        setIsDisplayNameFieldDisabled(true)
                                    }
                                    const formatted = formatDisplayName(
                                        getFieldValue('displayNameSelect'),
                                        getFieldValue('firstName'),
                                        getFieldValue('lastName')
                                    )
                                    if (formatted !== null) {
                                        setFieldValue('displayName', formatted)
                                    }
                                }

                                return Promise.resolve()
                            }
                        ]}
                    />
                </Space.Compact>
            </Form.Item>
        </>
    )
}
